import React, { memo } from 'react'

import { number, string, bool, node, func } from 'prop-types'

import NavbarIcon from 'Pods/UnifiedNavigation/Shared/NavbarIcon/NavbarIcon'

import { useActions } from './Hooks/useActions'
import { useCartScrollLock } from './Hooks/useCartScrollLock'

const ACTION_LABELS = {
  'cart-action': 'Cart',
  'inbox-action': 'Messages',
  'notifications-action': 'Notifications',
}
const ICON_SIZE = { width: '20', height: '20' }

// A single header action (cart / inbox / notifications). Renders a button trigger
// that opens its popover; the cart popover is wired here, inbox / notifications
// popovers land in a later phase.
function UserAction({
  id,
  icon,
  isForUser = false,
  isCurrentUser = false,
  count = 0,
  hasPopover = false,
  popoverComponent = null,
  isPopoverLoaded = false,
  onWidgetLoad = null,
  onOpen = null,
  bottomElement = null,
}) {
  const { isOpen, open, close, wrapperRef, triggerRef } = useActions(id)

  useCartScrollLock(id === 'cart-action' && isOpen)

  function handleClick() {
    if (!hasPopover) {
      return
    }

    if (isOpen) {
      close(id)
      return
    }

    function openPopover() {
      open(id)
      onOpen?.()
    }

    if (onWidgetLoad && !isPopoverLoaded) {
      onWidgetLoad(id, openPopover)
    } else {
      openPopover()
    }
  }

  function handleMouseEnter() {
    if (onWidgetLoad && !isPopoverLoaded) {
      onWidgetLoad(id)
    }
  }

  function handleTouchStart() {
    if (onWidgetLoad && !isPopoverLoaded) {
      onWidgetLoad(id)
    }
  }

  if (isForUser && !isCurrentUser) {
    return null
  }

  const hasBadge = count > 0
  const liClassName = id === 'cart-action'
    ? 'unified-nav__action js-shopping-cart-icon'
    : 'unified-nav__action'

  return (
    <li
      className={liClassName}
      id={id}
    >
      <button
        type="button"
        className="unified-nav__action-link"
        aria-label={ACTION_LABELS[id]}
        aria-haspopup={hasPopover ? 'true' : undefined}
        aria-expanded={hasPopover ? isOpen : undefined}
        onClick={handleClick}
        onMouseEnter={hasPopover ? handleMouseEnter : undefined}
        onTouchStart={hasPopover ? handleTouchStart : undefined}
        ref={triggerRef}
      >
        <NavbarIcon type={icon} size={ICON_SIZE} className="unified-nav__action-icon" />
        {hasBadge && (
          <span className="unified-nav__action-badge">{count}</span>
        )}
        {bottomElement}
      </button>
      {isOpen && (
        <div className="unified-nav__popover" ref={wrapperRef}>
          {popoverComponent}
        </div>
      )}
    </li>
  )
}

UserAction.propTypes = {
  id: string.isRequired,
  icon: string.isRequired,
  isForUser: bool,
  isCurrentUser: bool,
  count: number,
  hasPopover: bool,
  popoverComponent: node,
  isPopoverLoaded: bool,
  onWidgetLoad: func,
  onOpen: func,
  bottomElement: node,
}

export default memo(UserAction)
